import { useState, useEffect } from 'react';
import { Bell, BellOff, Wifi, WifiOff, Download, RefreshCw, CheckCircle, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  isOnline,
  notificationsSupported,
  getNotificationPermission,
  requestNotificationPermission,
  areNotificationsEnabled,
  setNotificationsEnabled,
  canInstallPWA,
  installPWA,
  isPWAInstalled,
  syncOfflineData,
  cacheDataForOffline
} from '../services/pwaService';
import { getOfflineQueue, clearOfflineQueue } from '../services/offlineDB';

// Offline Status Indicator (for use in Layout)
export const OfflineIndicator = () => {
  const [online, setOnline] = useState(isOnline());
  const [pendingSync, setPendingSync] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check pending items
    const checkPending = async () => {
      const queue = await getOfflineQueue();
      setPendingSync(queue.length);
    };
    checkPending();
    const interval = setInterval(checkPending, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  const handleSync = async () => {
    if (!online) {
      toast.error('Sin conexión a internet');
      return;
    }
    setSyncing(true);
    try {
      const result = await syncOfflineData();
      if (result.synced > 0) {
        toast.success(`${result.synced} cambios sincronizados`);
      }
      if (result.failed > 0) {
        toast.error(`${result.failed} cambios fallaron al sincronizar`);
      }
      // If all failed or nothing synced, offer to clear stale queue
      const queue = await getOfflineQueue();
      setPendingSync(queue.length);
    } catch (error) {
      toast.error('Error al sincronizar');
    }
    setSyncing(false);
  };

  const handleClearQueue = async () => {
    await clearOfflineQueue();
    setPendingSync(0);
    toast.success('Cola de sincronización limpiada');
  };

  if (online && pendingSync === 0) return null;

  return (
    <div className={`fixed bottom-4 left-4 z-50 flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg ${
      online ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
    }`}>
      {online ? (
        <Wifi className="w-4 h-4" />
      ) : (
        <WifiOff className="w-4 h-4" />
      )}
      <span className="text-sm font-medium">
        {online ? `${pendingSync} pendientes` : 'Sin conexión'}
      </span>
      {online && pendingSync > 0 && (
        <>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={handleSync}
            disabled={syncing}
            className="h-6 px-2"
            title="Sincronizar"
          >
            <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={handleClearQueue}
            className="h-6 px-2 text-red-600 hover:text-red-800"
            title="Limpiar cola (descartar cambios pendientes)"
          >
            <X className="w-3 h-3" />
          </Button>
        </>
      )}
    </div>
  );
};

// PWA Install Banner
export const PWAInstallBanner = () => {
  const [canInstall, setCanInstall] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const checkInstall = () => {
      setCanInstall(canInstallPWA() && !isPWAInstalled());
    };
    
    checkInstall();
    window.addEventListener('pwa-install-available', checkInstall);
    
    return () => {
      window.removeEventListener('pwa-install-available', checkInstall);
    };
  }, []);

  if (!canInstall || dismissed) return null;

  const handleInstall = async () => {
    const installed = await installPWA();
    if (installed) {
      toast.success('¡App instalada correctamente!');
      setCanInstall(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-sm">
      <Download className="w-5 h-5 flex-shrink-0" />
      <div className="flex-1">
        <p className="font-medium text-sm">Instalar Benivet</p>
        <p className="text-xs opacity-90">Acceso rápido y modo offline</p>
      </div>
      <Button size="sm" variant="secondary" onClick={handleInstall} className="text-teal-700">
        Instalar
      </Button>
      <button onClick={() => setDismissed(true)} className="p-1 hover:bg-white/20 rounded">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Notification Settings Component
export const NotificationSettings = () => {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      setSupported(notificationsSupported());
      setPermission(getNotificationPermission());
      const isEnabled = await areNotificationsEnabled();
      setEnabled(isEnabled);
      setLoading(false);
    };
    init();
  }, []);

  const handlePermissionRequest = async () => {
    const result = await requestNotificationPermission();
    setPermission(result);
    if (result === 'granted') {
      toast.success('Notificaciones activadas');
    } else if (result === 'denied') {
      toast.error('Notificaciones bloqueadas. Actívalas en la configuración del navegador.');
    }
  };

  const handleToggle = async (value) => {
    setEnabled(value);
    await setNotificationsEnabled(value);
    toast.success(value ? 'Notificaciones activadas' : 'Notificaciones desactivadas');
  };

  if (loading) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Bell className="w-5 h-5 text-teal-600" />
          Notificaciones de Medicación
        </CardTitle>
        <CardDescription>
          Recibe recordatorios cuando sea hora de administrar medicamentos
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!supported ? (
          <div className="bg-amber-50 p-3 rounded-lg text-amber-800 text-sm">
            <BellOff className="w-4 h-4 inline mr-2" />
            Tu navegador no soporta notificaciones push
          </div>
        ) : permission === 'denied' ? (
          <div className="bg-red-50 p-3 rounded-lg text-red-800 text-sm">
            <BellOff className="w-4 h-4 inline mr-2" />
            Notificaciones bloqueadas. Actívalas en la configuración de tu navegador.
          </div>
        ) : permission === 'default' ? (
          <Button onClick={handlePermissionRequest} className="w-full">
            <Bell className="w-4 h-4 mr-2" />
            Activar notificaciones
          </Button>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700">Notificaciones permitidas</span>
              </div>
              <Badge variant="outline" className="bg-green-50 text-green-700">Activo</Badge>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="notifications-toggle" className="font-medium">
                  Recordatorios de medicación
                </Label>
                <p className="text-xs text-slate-500 mt-1">
                  Recibir alertas cuando sea hora de dar medicamentos
                </p>
              </div>
              <Switch
                id="notifications-toggle"
                checked={enabled}
                onCheckedChange={handleToggle}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// PWA Status Card (for Settings page)
export const PWAStatusCard = () => {
  const [status, setStatus] = useState({
    online: true,
    installed: false,
    pendingSync: 0
  });
  const [caching, setCaching] = useState(false);

  useEffect(() => {
    const updateStatus = async () => {
      const queue = await getOfflineQueue();
      setStatus({
        online: isOnline(),
        installed: isPWAInstalled(),
        pendingSync: queue.length
      });
    };
    
    updateStatus();
    
    const handleOnline = () => updateStatus();
    const handleOffline = () => updateStatus();
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleCacheData = async () => {
    setCaching(true);
    try {
      await cacheDataForOffline();
      toast.success('Datos guardados para uso offline');
    } catch (error) {
      toast.error('Error guardando datos offline');
    }
    setCaching(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          {status.online ? (
            <Wifi className="w-5 h-5 text-green-600" />
          ) : (
            <WifiOff className="w-5 h-5 text-red-600" />
          )}
          Estado de Conexión
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="text-xs text-slate-500">Estado</p>
            <p className={`font-semibold ${status.online ? 'text-green-600' : 'text-red-600'}`}>
              {status.online ? 'Conectado' : 'Sin conexión'}
            </p>
          </div>
          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="text-xs text-slate-500">App instalada</p>
            <p className={`font-semibold ${status.installed ? 'text-green-600' : 'text-slate-600'}`}>
              {status.installed ? 'Sí' : 'No'}
            </p>
          </div>
        </div>
        
        {status.pendingSync > 0 && (
          <div className="bg-amber-50 p-3 rounded-lg">
            <p className="text-sm text-amber-800">
              <RefreshCw className="w-4 h-4 inline mr-2" />
              {status.pendingSync} cambios pendientes de sincronizar
            </p>
          </div>
        )}
        
        <Button 
          variant="outline" 
          onClick={handleCacheData} 
          disabled={caching || !status.online}
          className="w-full"
        >
          <Download className={`w-4 h-4 mr-2 ${caching ? 'animate-bounce' : ''}`} />
          {caching ? 'Guardando...' : 'Guardar datos para offline'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
